import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useRegisteredShortcuts } from 'hooks/useKeyboardShortcuts';
import { useHelpDialogCommandPaletteActions } from 'hooks/commandPaletteActions';
import { useTranslation } from 'react-i18next';
import { ForcedTheme } from 'components/ForcedTheme';
import {
  CommandPaletteAction,
  CommandPaletteActionCategory,
} from 'components/CommandPaletteDialog/commandPaletteAction.types';

export const KeyboardShortcutsDialog = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  useHelpDialogCommandPaletteActions(() => setIsOpen((prev) => !prev));

  const categoryOrder: Array<CommandPaletteActionCategory> = [
    'common',
    'baseMap',
    'mapLayers',
    'teamLayers',
    'mapTools',
    'camera',
  ];

  const shortcutsByCategory: Record<
    CommandPaletteActionCategory,
    CommandPaletteAction[]
  > = useRegisteredShortcuts()
    // Command-palette-only entries have no keys, so skip them.
    .filter((shortcut) => shortcut.keys)
    .reduce(
      (acc, shortcut) => {
        const { category } = shortcut;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(shortcut);
        return acc;
      },
      {} as Record<CommandPaletteActionCategory, CommandPaletteAction[]>,
    );

  // Sort shortcuts by keys within each category
  categoryOrder.forEach((category) => {
    shortcutsByCategory[category]?.sort((a, b) =>
      (a.keys ?? '').localeCompare(b.keys ?? ''),
    );
  });
  const shortcutsByCategoryInOrder = categoryOrder
    .map((category) => [category, shortcutsByCategory[category]] as const)
    .filter(([, shortcuts]) => shortcuts?.length);

  return (
    <ForcedTheme mode="dark">
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        disablePortal
        slotProps={{
          paper: {
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle>{t('keyboardShortcuts.dialog.title')}</DialogTitle>
        <DialogContent>
          {shortcutsByCategoryInOrder.map(([category, shortcuts]) => (
            <Grid key={category} container padding={0.5} sx={{ width: '100%' }}>
              <Grid size={12} marginBottom={0}>
                <Typography component="h4" sx={{ fontWeight: 'bold' }}>
                  {t(`keyboardShortcuts.categories.${category}`)}
                </Typography>
              </Grid>
              <Grid size={12} padding={0}>
                <List>
                  {shortcuts.map(({ description, keys }) => (
                    <ListItem
                      key={`${description}-${keys}`}
                      sx={{
                        marginBottom: -1,
                      }}
                    >
                      <Box
                        sx={{
                          width: '150px',
                          display: 'flex',
                        }}
                      >
                        <Chip variant="keyboard-shortcut" label={keys} />
                      </Box>
                      <ListItemText primary={description} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            color="neutral"
            onClick={() => setIsOpen(false)}
            sx={{ mb: 1 }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </ForcedTheme>
  );
};
